import { useEffect, useState, type ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import { errorMessage } from '../../../lib/api';
import { useCropAvatar, useUploadAvatar } from '../../onboarding/user/api';
import type { AvatarCrop, Profile } from '../../onboarding/user/types';
import { AvatarCropDialog, type CropSource } from './AvatarCropDialog';
import styles from './avatarUpload.module.css';

/** Avatar upload rules of Nutzer 02/03 and Einstellungen 01/02 (the server repeats them). */
export const AVATAR_MIN_PX = 400;
export const AVATAR_TYPES = ['image/jpeg', 'image/png'];
export const AVATAR_TYPE_ERROR = 'Bitte laden Sie ein JPG oder PNG hoch.';

/** „portrait.jpg ist 240 × 240 px — zu klein" (Nutzer 03) — the server's sentence. */
export function tooSmallMessage(name: string, width: number, height: number): string {
  return `${name} ist ${width} × ${height} px — zu klein`;
}

function imageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error(AVATAR_TYPE_ERROR));
    image.src = url;
  });
}

/** The stored avatar rendered with its crop (a square of the source shown in a circle). */
export function CroppedAvatar({
  url,
  crop,
  size,
  className,
  alt,
}: {
  url: string;
  crop: AvatarCrop | null;
  size: number;
  className?: string | undefined;
  alt: string;
}) {
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const square = natural ? (crop ?? centred(natural)) : null;
  const scale = square ? size / square.size : 1;
  return (
    <span className={clsx(styles.cropped, className)} style={{ width: size, height: size }}>
      <img
        src={url}
        alt={alt}
        draggable={false}
        onLoad={(event) =>
          setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })
        }
        style={
          natural && square
            ? {
                width: natural.width * scale,
                height: natural.height * scale,
                left: -square.x * scale,
                top: -square.y * scale,
              }
            : { visibility: 'hidden' }
        }
      />
    </span>
  );
}

function centred(natural: { width: number; height: number }): AvatarCrop {
  const size = Math.min(natural.width, natural.height);
  return { x: Math.round((natural.width - size) / 2), y: Math.round((natural.height - size) / 2), size };
}

export interface AvatarFlow {
  /** Validation or upload error under the picker („… — zu klein"). */
  error: string | null;
  /** Hands a chosen or dropped file to the flow: checks type and size, then opens the crop dialog. */
  choose: (file: File) => void;
  /** Re-crops the stored avatar (Einstellungen 01 has no separate button; used after a fresh upload). */
  recrop: (profile: Profile) => void;
  dialog: ReactNode;
}

/**
 * File → checks (type, 400 px) → Einstellungen 02 crop dialog → `POST /settings/profile/avatar` with the crop
 * (or `PATCH` for a re-crop of the stored image). `onSaved` runs after a stored change.
 */
export function useAvatarFlow(onSaved?: () => void): AvatarFlow {
  const upload = useUploadAvatar();
  const recropMutation = useCropAvatar();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    file: File | null;
    source: CropSource;
    crop: AvatarCrop | null;
  } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (pending?.file) URL.revokeObjectURL(pending.source.url);
    },
    [pending],
  );

  function choose(file: File) {
    setError(null);
    setDialogError(null);
    if (!AVATAR_TYPES.includes(file.type)) {
      setError(AVATAR_TYPE_ERROR);
      return;
    }
    const url = URL.createObjectURL(file);
    imageSize(url)
      .then(({ width, height }) => {
        if (width < AVATAR_MIN_PX || height < AVATAR_MIN_PX) {
          URL.revokeObjectURL(url);
          setError(tooSmallMessage(file.name, width, height));
          return;
        }
        setPending({ file, source: { url, name: file.name, bytes: file.size }, crop: null });
      })
      .catch((reason: unknown) => {
        URL.revokeObjectURL(url);
        setError(reason instanceof Error ? reason.message : AVATAR_TYPE_ERROR);
      });
  }

  function recrop(profile: Profile) {
    if (!profile.avatar) return;
    setDialogError(null);
    setPending({
      file: null,
      source: { url: profile.avatar.url, name: 'Profilbild', bytes: null },
      crop: profile.avatar.crop,
    });
  }

  function apply(crop: AvatarCrop) {
    if (!pending) return;
    const done = () => {
      setPending(null);
      onSaved?.();
    };
    const fail = (reason: unknown) => {
      setDialogError(errorMessage(reason));
    };
    if (pending.file) {
      upload.mutateAsync({ file: pending.file, crop }).then(done, (reason: unknown) => {
        // A refused file (size, type) belongs under the picker; the dialog closes.
        setPending(null);
        setError(errorMessage(reason));
      });
    } else {
      recropMutation.mutateAsync(crop).then(done, fail);
    }
  }

  const dialog = (
    <AvatarCropDialog
      open={pending !== null}
      source={pending?.source ?? null}
      initialCrop={pending?.crop ?? null}
      busy={upload.isPending || recropMutation.isPending}
      error={dialogError}
      onClose={() => setPending(null)}
      onApply={apply}
    />
  );

  return { error, choose, recrop, dialog };
}
